import React, {FC, useEffect, useRef, useState} from 'react';
import {MapProps as GLMapProps, MapRef} from 'react-map-gl';
import classNames from 'classnames';
import {BaseMap} from 'components/BaseMap';
import {Location} from 'components/TimePicker/types';
import {useWindowSize} from 'hooks/useWindowSize';
import mapboxgl from 'mapbox-gl';
import {useContextLocation} from 'pages/booking/components/useLocation';

import Details, {UserDetails} from './Details';
import {createMarker, createUserMarker, userMarkerId} from './markers';

import * as styles from './styles.module.scss';

export type MapProps = {
	className?: string
	region?: string
	init?: {
		coords?: number[]
		zoom?: number
	}
	autoHide?: number
	locations: Location[]
	mapProps?: GLMapProps
}

const Map: FC<MapProps> = ({className, region, init, autoHide, locations, mapProps}) => {
	const {configs,} = useContextLocation();
	const [selected, setSelected] = useState<Location | null>(null);
	const mapRef = useRef<MapRef>(null);
	const [mapLoaded, setLoaded] = useState<boolean>(false);
	const winSize = useWindowSize();
	const [hidden, setHidden] = useState<boolean>(Boolean(autoHide));

	// If autoHide is set, hide if width too small
	useEffect(() => {
		if (autoHide && winSize.width) {
			setHidden(winSize.width < autoHide);
		} else {
			setHidden(false);
		}
	}, [autoHide, winSize.width]);

	// As locations load, flyTo their centroid
	useEffect(() => {
		if (!mapRef.current || locations.length === 0) {
			return;
		} else if (!region) {
			return;
		}

		const maxZoom = init?.zoom || 7.5;
		if (init?.coords && init.coords.length === 2) {
			if (mapRef.current.isMoving()) {
				return;
			}

			mapRef.current.flyTo({
				center: [
					init.coords[0],
					init.coords[1],
				],
				zoom: maxZoom,
			});
			return;
		}

		const bounds = new mapboxgl.LngLatBounds();
		locations.forEach(({details: {coords}}) => {
			if (coords.lng && coords.lat) {
				bounds.extend([coords.lng, coords.lat]);
			}
		});

		mapRef.current.fitBounds(bounds, {maxZoom});
	}, [
		mapRef.current,
		mapLoaded,
		locations.length,
		region,
		init?.coords,
		init?.zoom,
	]);

	// If hidden, return null to remove from DOM
	if (hidden) {
		return null;
	}

	// Compute initial set of markers; one per location.
	// If any are selected, render the additional details.
	let markers = locations
		.filter((l) => l.datesLeft.length > 0)
		.map(createMarker({
			selected,
			setSelected,
			mapRef,
		}));

	// Add user marker for searched location
	if (configs.coords) {
		markers = [...markers, createUserMarker(
			{
				setSelected,
				selected,
				mapRef,
			},
			configs.coords,
		)];
	}

	if (selected) {
		let selectedMarker, children;
		if (selected.id !== userMarkerId) {
			selectedMarker = createMarker({
				selected,
				setSelected,
				mapRef,
			})(selected);

			children = (
				<Details
					location={selected}
					onClose={() => setSelected(null)}
				/>
			);
		} else if (configs.coords) {
			selectedMarker = createUserMarker({
				selected,
				setSelected,
				mapRef,
			}, configs.coords);

			children = (
				<UserDetails
					config={configs}
					onClose={() => setSelected(null)}
				/>
			);
		}

		if (selectedMarker && children) {
			markers = [...markers, {
				...selectedMarker,
				id: 'selected',
				anchor: 'bottom',
				offset: [0, -24],
				style: {zIndex: 2},
				children: children,
			}];
		}
	}

	return (
		<BaseMap
			className={classNames(styles.map, className)}
			markers={markers}

			// Check by using maps link like:
			// https://www.google.com/maps/@38.2,-98.1,2.5z
			initialViewState={{
				latitude: 38.2,
				longitude: -98.1,
				zoom: 2.5,
			}}

			mapRef={mapRef}
			controls={{nav: true}}
			onLoad={() => setLoaded(true)}
			{...mapProps}
		/>
	);
};

export default Map;
